import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { askAiHttpClient } from 'api/ai-chat';
import { aiAnalystsHttpClient } from 'api/ai-chat/index-ai-analysts';
import { GENERAL_API_STALE } from 'config/query-config';
import { isTauriApp } from 'utils/is-tauri';
import { emitAgentChatHistoryUpdated } from 'utils/quick-entry-native';
export const CHAT_QUERY_KEYS = {
    all: [
        'ai_agent_chat'
    ],
    conversations: ()=>[
            ...CHAT_QUERY_KEYS.all,
            'conversations'
        ],
    conversationHistory: (conversationId)=>[
            ...CHAT_QUERY_KEYS.all,
            'conversation',
            conversationId
        ],
    chatHistory: (variant)=>[
            ...CHAT_QUERY_KEYS.all,
            'history',
            variant || 'default'
        ],
    searchConversations: (accountId, userId, query, agent)=>[
            ...CHAT_QUERY_KEYS.all,
            'search',
            accountId,
            userId,
            query,
            agent
        ],
    analysts: (role)=>[
            ...CHAT_QUERY_KEYS.all,
            'analysts',
            role || 'all'
        ]
};
export const agentChatHistoryQueryKey = (agentName)=>[
        ...CHAT_QUERY_KEYS.all,
        'history',
        agentName
    ];
/**
 * Optimistically put a newly created chat at the top of the agent history list
 * so the Nitro Claw sidebar updates before the backend list refetch returns.
 */ export const prependAgentConversationToHistory = (queryClient, agentName, conversation, options)=>{
    var _conversation_created_at, _conversation_updated_at;
    const queryKey = agentChatHistoryQueryKey(agentName);
    const entry = {
        ...conversation,
        conversation_id: conversation.conversation_id,
        title: conversation.title,
        created_at: (_conversation_created_at = conversation.created_at) !== null && _conversation_created_at !== void 0 ? _conversation_created_at : new Date().toISOString(),
        updated_at: (_conversation_updated_at = conversation.updated_at) !== null && _conversation_updated_at !== void 0 ? _conversation_updated_at : null
    };
    let didInsert = false;
    queryClient.setQueryData(queryKey, (current)=>{
        var _firstPage_conversations, _firstPage_total;
        var _current_pages;
        if (!(current === null || current === void 0 ? void 0 : (_current_pages = current.pages) === null || _current_pages === void 0 ? void 0 : _current_pages.length)) {
            didInsert = true;
            return {
                pages: [
                    {
                        has_more: false,
                        page: 1,
                        page_size: 40,
                        total: 1,
                        conversations: [
                            entry
                        ]
                    }
                ],
                pageParams: [
                    0
                ]
            };
        }
        const alreadyListed = current.pages.some((page)=>{
            var _page_conversations;
            return ((_page_conversations = page.conversations) !== null && _page_conversations !== void 0 ? _page_conversations : []).some((item)=>item.conversation_id === entry.conversation_id);
        });
        if (alreadyListed) {
            if (conversation.title == null && conversation.updated_at == null) {
                return current;
            }
            return {
                ...current,
                pages: current.pages.map((page)=>{
                    var _page_conversations;
                    return {
                        ...page,
                        conversations: ((_page_conversations = page.conversations) !== null && _page_conversations !== void 0 ? _page_conversations : []).map((item)=>item.conversation_id === entry.conversation_id ? {
                                ...item,
                                ...conversation.title != null ? {
                                    title: conversation.title
                                } : {},
                                ...conversation.updated_at != null ? {
                                    updated_at: conversation.updated_at
                                } : {}
                            } : item)
                    };
                })
            };
        }
        didInsert = true;
        const [firstPage, ...restPages] = current.pages;
        return {
            ...current,
            pages: [
                {
                    ...firstPage,
                    conversations: [
                        entry,
                        ...(_firstPage_conversations = firstPage.conversations) !== null && _firstPage_conversations !== void 0 ? _firstPage_conversations : []
                    ],
                    total: ((_firstPage_total = firstPage.total) !== null && _firstPage_total !== void 0 ? _firstPage_total : 0) + 1
                },
                ...restPages
            ]
        };
    });
    // Separate Tauri windows (main vs NSPanel) do not share React Query cache.
    if (didInsert && (options === null || options === void 0 ? void 0 : options.broadcast) !== false && isTauriApp()) {
        void emitAgentChatHistoryUpdated({
            agentName,
            conversation: {
                conversation_id: entry.conversation_id,
                title: entry.title,
                created_at: entry.created_at
            }
        });
    }
};
export const useChatHistoryInfiniteQuery = (params, options)=>{
    return useInfiniteQuery({
        queryKey: CHAT_QUERY_KEYS.chatHistory(),
        queryFn: ({ pageParam = 0 })=>askAiHttpClient.getAllConversations({
                ...params,
                offset: pageParam
            }),
        getNextPageParam: (lastPage, allPages)=>{
            // Check if lastPage and conversations exist
            if (!lastPage || !lastPage.conversations) {
                return undefined;
            }
            // If we got a full page of results, there might be more
            if (lastPage.conversations.length === params.limit) {
                // Calculate the next offset based on total items loaded so far
                const totalItemsLoaded = allPages.reduce((sum, page)=>{
                    var _page_conversations;
                    return sum + (((_page_conversations = page.conversations) === null || _page_conversations === void 0 ? void 0 : _page_conversations.length) || 0);
                }, 0);
                return totalItemsLoaded;
            }
            return undefined;
        },
        // select: (data) => ({
        //   ...data,
        //   conversations: data.pages.flatMap((page) => page.conversations || []),
        // }),
        staleTime: GENERAL_API_STALE,
        cacheTime: GENERAL_API_STALE,
        ...options
    });
};
export const useAgentChatHistoryInfiniteQuery = (params, agentName, options)=>{
    const { ...restOptions } = options || {};
    return useInfiniteQuery({
        queryKey: [
            ...CHAT_QUERY_KEYS.all,
            'history',
            agentName
        ],
        queryFn: ({ pageParam = 0 })=>{
            const offset = pageParam;
            return aiAnalystsHttpClient.getAllAnalyticsConversations({
                limit: params.limit,
                offset,
                agent_name: agentName
            });
        },
        getNextPageParam: (lastPage, allPages)=>{
            if (!lastPage || !lastPage.conversations) {
                return undefined;
            }
            if (lastPage.conversations.length === params.limit) {
                const totalItemsLoaded = allPages.reduce((sum, page)=>{
                    var _page_conversations;
                    return sum + (((_page_conversations = page.conversations) === null || _page_conversations === void 0 ? void 0 : _page_conversations.length) || 0);
                }, 0);
                return totalItemsLoaded;
            }
            return undefined;
        },
        // Conversation lists change whenever a chat is created/deleted — do not
        // treat cached pages as fresh for an hour (GENERAL_API_STALE).
        staleTime: 0,
        cacheTime: GENERAL_API_STALE,
        refetchOnMount: 'always',
        ...restOptions
    });
};
export const useAnalystsQuery = (role, options)=>{
    return useQuery({
        queryKey: CHAT_QUERY_KEYS.analysts(role),
        queryFn: ()=>{
            if (role === 'ai_analyst') {
                return aiAnalystsHttpClient.getAnalysts();
            }
            return aiAnalystsHttpClient.getSignalAgents();
        },
        staleTime: GENERAL_API_STALE,
        cacheTime: GENERAL_API_STALE,
        ...options
    });
};
